/* eslint-disable no-console */

import { LedgerConnectionFactory } from '@domains/ledger-platform'
import { Tooltip, Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { useQueryClient } from '@tanstack/react-query'
import { Access, usePermissions } from 'contexts/permissions'
import dayjs from 'dayjs'
import { Formik, type FormikProps } from 'formik'
import useMaterialNotification from 'hooks/useMaterialNotification'
import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { deleteCustomerUsingDELETE } from '../api'
import {
  useCardCredits,
  useInvoiceConfig,
  usePatchActivationGuideVisibility,
  usePatchCardCredit,
  usePatchInvoiceConfig,
  useRefreshAddress,
  useSendWelcomeEmail,
} from '../api/react-query'
import { ACTIVATION_GUIDE, CUSTOMER_V2 } from '../api/react-query/keys'
import {
  type ActivationGuideDto,
  ApprovalStatus,
  type BackOfficeCustomerDtoV2,
  BackofficeInvoiceDeliveryMethod,
  BillingSettingsPricePlan,
  CardCreditType,
  CardPartner,
  CustomerFeature,
  CustomerProduct,
  KycAccessLevel,
  type Visibility,
} from '../api/swagger/definitions/backoffice'
import config from '../config'
import ChurnCustomerIcon from '../figma/images/churnCustomerIcon'
import DeleteCustomerIcon from '../figma/images/deleteCustomerIcon'
import iconButtonEmail from '../figma/images/iconButtonEmail'
import iconButtonrefreshAddress from '../figma/images/iconButtonrefreshAddress'
import iconButtonrefreshAddressHover from '../figma/images/iconButtonrefreshAddressHover'
import Spacings from '../figma/tokens/Spacings'
import { accountStatusToColor } from '../helpers/AccountStatusHelper'
import {
  BorderedContainer,
  customerHasProduct,
  DeleteCustomerPopper,
  Divider,
  LinkButton,
  objectValueReplace,
  StyledMaterialFieldFormik,
} from '../helpers/customerGeneralFormHelper'
import TextKeys from '../libs/TextKeys'
import FigmaBox from '../mynt-components/components/FigmaBox'
import MaterialSelectFieldFormik from '../mynt-components/components/MaterialSelectFieldFormik'
import MaterialSwitchFormik from '../mynt-components/components/MaterialSwitchFormik'
import MyntPopper from '../mynt-components/components/MyntPopper'
import { UnstyledButton } from '../mynt-components/components/StyledComponents'
import SVGContainer from '../mynt-components/components/SVGContainer'
import { CUSTOMERS_PATH } from '../routes/constants'
import ChurnCustomerModal from './ChurnCustomerModal'
import { ModalConfirmDialog } from './Modals'
import { SelectController, TextFieldController } from './react-hook-components'
import SendWelcomeEmailPopperContent from './SendWelcomeEmailPopperContent'
import TextContainer from './TextContainer'
import { TooltipRefreshAddress } from './Tooltips'

type CustomerGeneralFormProps = {
  customer: BackOfficeCustomerDtoV2
  activationGuideVisibility?: ActivationGuideDto
  customerMutation: (partialCustomer: Partial<BackOfficeCustomerDtoV2>) => Promise<unknown>
}

// Replace with real enum in future
enum VisibilityEnum {
  COMPLETED = 'COMPLETED',
  HIDDEN = 'HIDDEN',
  VISIBLE = 'VISIBLE',
}

const InvoicingActiveOptions = [
  { value: 'ON', label: 'On' },
  { value: 'OFF', label: 'Off' },
]

const toInvoicingActiveOption = (value?: boolean | null) => (value == null ? '' : value ? 'ON' : 'OFF')

const DeliveryMethodOptions = [
  { value: BackofficeInvoiceDeliveryMethod.EMAIL, label: 'Email' },
  { value: BackofficeInvoiceDeliveryMethod.E_INVOICE, label: 'E-invoice' },
]

const toDeliveryMethodOption = (value?: BackofficeInvoiceDeliveryMethod | null) => value ?? ''

type InvoiceConfigFormValues = {
  eInvoiceAddress: string
  deliveryMethod: BackofficeInvoiceDeliveryMethod | ''
}

/**
 * The following values are sorted in a logical order, best to worst in general i.e feature disabling option is at the bottom.
 *
 * The arrays are reversed since the goal is setting the disabled value at the end only works when its at the start,
 * since sets can only have unique values, spreading all the values,
 * and then adding them again, just removes it the manually placed value and we are left with the original enum.
 *
 * By reversing the arrays, we ensure all the values are unique and are in the right order.
 * Since the value we placed first, are now in the end, resulting in want we want.
 *
 * The reasoning behind this sortof complex strategy is future proofing,
 * if the enums are extended in the future we ensure that they are included, although in the wrong order.
 */

const cardPartnerExcludeFilter = [CardPartner.ACCOUNTEC, CardPartner.TRIPLETEX, CardPartner.BOLAGERIET]
const filteredCardPartnerValues = Object.values(CardPartner).filter((value) => !cardPartnerExcludeFilter.includes(value))

const sortedCardPartnerValues = Array.from(new Set([CardPartner.NONE, ...filteredCardPartnerValues])).reverse()

const sortedApprovalStatus = Array.from(
  new Set([
    ApprovalStatus.AUTO_DENIED,
    ApprovalStatus.MANUALLY_DENIED,
    ApprovalStatus.PENDING,
    ApprovalStatus.AUTO_APPROVED,
    ApprovalStatus.MANUALLY_APPROVED,
    ...Object.values(ApprovalStatus),
  ]),
).reverse()

const sortedActivationGuideValue = Array.from(
  new Set([VisibilityEnum.COMPLETED, VisibilityEnum.VISIBLE, VisibilityEnum.HIDDEN, ...Object.values(VisibilityEnum)]),
)

const subscriptonPlanValues = Object.values(BillingSettingsPricePlan)

const kycAccessLevelOptions = [
  { value: KycAccessLevel.GREEN, label: 'Approved' },
  { value: KycAccessLevel.YELLOW, label: 'Pending' },
  { value: KycAccessLevel.RED, label: 'Denied' },
  { value: KycAccessLevel.GREY, label: 'Not done' },
]

// These came from jakob (https://trello.com/c/7c6Qnt0R/3752-improve-backoffice-and-meaning-of-products-features-etc)
const FIGMA_LEFT_COLUMN_WIDTH = 782

const CustomerGeneralForm: React.FC<CustomerGeneralFormProps> = ({ customer, activationGuideVisibility, customerMutation }) => {
  const [canEditToken, setCanEditToken] = useState(false)
  const [showTokenPopper, setShowTokenPopper] = useState(false)
  const [showDeleteCustomerPopper, setShowDeleteCustomerPopper] = useState(false)
  const [showSendWelcomeEmailPopper, setShowSendWelcomeEmailPopper] = useState(false)
  const [hover, setHover] = useState(false)
  const [isChurnModalOpen, setIsChurnModalOpen] = useState(false)

  const permissions = {
    activationGuide: usePermissions('customers.activation-guide'),
    cardPartner: usePermissions('customers.card-partner'),
    subscription: usePermissions('customers.subscription'),
  }

  const notify = useMaterialNotification()
  const queryClient = useQueryClient()

  const tokenElementRef = useRef(null)
  const deleteCustomerElementRef = useRef(null)
  const churnCustomerElementRef = useRef(null)
  const sendWelcomeEmailElementRef = useRef(null)
  const tokenRef = useRef<HTMLInputElement>(null)

  const activationGuideMutation = usePatchActivationGuideVisibility(customer.id)
  const sendWelcomeEmailMutation = useSendWelcomeEmail(customer.id)

  const { data: invoiceConfig } = useInvoiceConfig(customer.id)
  const invoiceConfigMutation = usePatchInvoiceConfig(customer.id)

  const invoiceConfigForm = useForm<InvoiceConfigFormValues>({
    defaultValues: {
      eInvoiceAddress: invoiceConfig?.eInvoiceAddress || '',
      deliveryMethod: toDeliveryMethodOption(invoiceConfig?.deliveryMethod),
    },
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore existing biome errors
  useEffect(() => {
    invoiceConfigForm.reset({
      eInvoiceAddress: invoiceConfig?.eInvoiceAddress || '',
      deliveryMethod: toDeliveryMethodOption(invoiceConfig?.deliveryMethod),
    })
  }, [invoiceConfig])

  const customerHasCardProduct = Array.from(customer.products).includes(CustomerProduct.CARD)
  const { data: cardCredits } = useCardCredits(customer.id, { enabled: customerHasCardProduct })
  const cardCredit = cardCredits?.[0]
  const cardCreditMutation = usePatchCardCredit(customer.id)

  const invoicingActiveForm = useForm<{ isInvoicingActive: string }>({
    defaultValues: { isInvoicingActive: toInvoicingActiveOption(cardCredit?.isInvoicingActive) },
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore existing biome errors
  useEffect(() => {
    invoicingActiveForm.reset({ isInvoicingActive: toInvoicingActiveOption(cardCredit?.isInvoicingActive) })
  }, [cardCredit])

  const onInvoicingActiveUpdate = invoicingActiveForm.handleSubmit((data) => {
    if (!cardCredit || data.isInvoicingActive === '') return

    const isInvoicingActive = data.isInvoicingActive === 'ON'

    if (isInvoicingActive === cardCredit.isInvoicingActive) return

    cardCreditMutation.mutateAsync({ isInvoicingActive, creditId: cardCredit.id }).then(() => {
      notify('Card credit has been updated')
    })
  })

  const invoicingActiveDisabledByExternal = cardCredit?.creditType === CardCreditType.EXTERNAL

  const addressMutation = useRefreshAddress(customer.id)
  const navigate = useNavigate()

  const features = useMemo(() => Array.from(customer.features), [customer])
  const products = useMemo(() => Array.from(customer.products), [customer])

  const initialValues = useMemo(() => {
    const replacer = (value: any) => (value === null ? '' : value)

    const initial = {
      visibility: activationGuideVisibility?.visibility,
      kycAccessLevel: customer.kycAccessLevel,
      companyName: customer.companyName,
      addressField1: customer.addressField1,
      postCode: customer.postCode,
      orgNo: customer.orgNo,
      addressField2: customer.addressField2,
      postTown: customer.postTown,
      postCountry: customer.postCountry,
      invoiceEmail: customer.invoiceEmail,
      phoneNumber: customer.phoneNumber,
      bankAccount: customer.bankAccount,
      bankgiro: customer.bankgiro,
      channel: customer.channel,
      cardPartner: customer.cardPartner,
      cardPartnerId: customer.cardPartnerId,
      cardPartnerToken: customer.cardPartnerToken,
      cardProduct: products.includes(CustomerProduct.CARD),
      cardTopup: features.includes(CustomerFeature.TOPUP),
      orderCard: features.includes(CustomerFeature.CARD_ORDER),
      cardCredit: products.includes(CustomerProduct.CARD_CREDIT),
      multiInvoiceCredit: products.includes(CustomerProduct.MULTI_INVOICE_CREDIT),
    }

    return objectValueReplace(initial, replacer) as typeof initial
  }, [customer, features, products, activationGuideVisibility])

  type InitialValuesType = typeof initialValues

  const [loaders, setLoaders] = useState<Partial<Record<keyof InitialValuesType, boolean>>>({})

  const productsMapping: Record<Exclude<CustomerProduct, 'COMPANY_CREDIT'>, keyof InitialValuesType> = {
    [CustomerProduct.CARD]: 'cardProduct',
    [CustomerProduct.CARD_CREDIT]: 'cardCredit',
    [CustomerProduct.MULTI_INVOICE_CREDIT]: 'multiInvoiceCredit',
  }

  const featuresMapping: Record<Exclude<CustomerFeature, 'RECLAIM_CREDIT'>, keyof InitialValuesType> = {
    [CustomerFeature.TOPUP]: 'cardTopup',
    [CustomerFeature.CARD_ORDER]: 'orderCard',
  }

  const productArrayValues: (keyof InitialValuesType)[] = ['cardProduct', 'cardCredit', 'multiInvoiceCredit']
  const featureArrayValues: (keyof InitialValuesType)[] = ['cardTopup', 'orderCard']

  const fieldToAPIMapping: Partial<Record<keyof InitialValuesType, keyof BackOfficeCustomerDtoV2>> = {
    cardProduct: 'products',
    cardTopup: 'features',
    orderCard: 'features',
    cardCredit: 'products',
    multiInvoiceCredit: 'products',
  }

  // Add more methods here to format any of the fields if they need special formatting
  const formatValue = (name: keyof InitialValuesType, value: any) => {
    switch (name) {
      case 'cardProduct': {
        if (customerHasProduct(customer)) return value

        return Array.from(new Set([...value, CustomerProduct.CARD_CREDIT]))
      }

      default: {
        return value
      }
    }
  }

  /**
   * Sets a loading time for a field
   * @param name name of the field
   * @param promise the mutation promise
   */
  const setLoader = (name: keyof InitialValuesType, promise: Promise<any>) => {
    setLoaders((currentLoaders) => ({ ...currentLoaders, [name]: true }))

    promise.finally(() => {
      setLoaders((currentLoaders) => ({ ...currentLoaders, [name]: false }))
    })
  }

  const handleActivationGuideStatus =
    (name: keyof InitialValuesType, formProps: FormikProps<any>) => (event: React.ChangeEvent<HTMLSelectElement>) => {
      const visibility = event.target.value as Visibility

      activationGuideMutation.mutateAsync({ visibility }).then(() => {
        notify(`Activation guide visibility set to ${visibility}`)
      })
    }

  const handleCustomerDelete = () => {
    setShowDeleteCustomerPopper(false)
    deleteCustomerUsingDELETE(customer.id).then(() => {
      navigate(CUSTOMERS_PATH)
    })
  }

  const handleOnCustomerChurnSuccess = () => {
    setIsChurnModalOpen(false)

    queryClient.invalidateQueries({
      queryKey: [CUSTOMER_V2, customer.id],
    })
    queryClient.invalidateQueries({
      queryKey: [ACTIVATION_GUIDE, customer.id],
    })

    notify(`Churned customer ${customer.companyName} successfully`)
  }

  const handleSendWelcomeEmail = () => {
    sendWelcomeEmailMutation.mutateAsync({ customerId: customer.id }).then(() => setShowSendWelcomeEmailPopper(false))
  }

  const handleEditCompanyToken = () => {
    setCanEditToken(!canEditToken)

    const input = tokenRef.current?.querySelector('input')

    setTimeout(() => input?.focus())
  }

  const handleEditTokenDiscard = (formProps: FormikProps<InitialValuesType>) => {
    setCanEditToken(false)
    setShowTokenPopper(false)

    formProps.setFieldValue('cardPartnerToken', initialValues.cardPartnerToken)
  }

  const handleEditTokenSave = (formProps: FormikProps<InitialValuesType>) => {
    setCanEditToken(false)
    setShowTokenPopper(false)

    const promise = customerMutation({
      cardPartnerToken: formProps.values.cardPartnerToken,
    })

    setLoader('cardPartnerToken', promise)

    promise.catch(() => formProps.resetForm())

    // We need to reset the form since the value is masked on the backend
    promise.finally(() => {
      formProps.resetForm()
    })
  }

  const handleInvoicePatchInvoiceConfig = ({ eInvoiceAddress }: InvoiceConfigFormValues) => {
    if ((invoiceConfig?.eInvoiceAddress || '') === eInvoiceAddress) return

    invoiceConfigMutation.mutateAsync({ eInvoiceAddress })
  }

  const onDeliveryMethodUpdate = (event: { target: { value: unknown } }) => {
    const deliveryMethod = event.target.value as BackofficeInvoiceDeliveryMethod

    if (!Object.values(BackofficeInvoiceDeliveryMethod).includes(deliveryMethod)) return
    if (deliveryMethod === invoiceConfig?.deliveryMethod) return

    invoiceConfigMutation.mutateAsync({ deliveryMethod })
  }

  // Any select fields require their own onChange handler, onBlur doesnt work
  const createSelectChangeHandler =
    (name: keyof InitialValuesType, formProps: FormikProps<any>) => (event: React.ChangeEvent<HTMLSelectElement>) => {
      formProps.handleChange(name)(event)

      const newValue = event.target.value
      const oldValue = initialValues[name]

      if (newValue === oldValue) return console.log('no diff')

      // Handle any formatting edgecases, default to the string value
      const formattedValue = formatValue(name, newValue)

      const promise = customerMutation({
        [name]: formattedValue,
      })

      setLoader(name, promise)

      promise.catch(() => formProps.resetForm())
    }

  const createBlurHandler = (name: keyof InitialValuesType, formProps: FormikProps<InitialValuesType>) => async (event: any) => {
    // If the value is in an array, send it to the array handler
    if ([...productArrayValues, ...featureArrayValues].includes(name)) {
      return blurHandlerArray(name, formProps, event)
    }

    formProps.validateForm().then((errors) => {
      if (Object.values(errors).length) return

      const newValue = formProps.values[name]
      const oldValue = initialValues[name]

      if (newValue === oldValue) return console.log('no diff')

      // Handle any formatting edgecases, default to the string value
      const formattedValue = formatValue(name, newValue)

      // Empty strings should be null
      const promise = customerMutation({
        [name]: formattedValue,
      })

      setLoader(name, promise)

      promise.catch(() => formProps.resetForm())
    })
  }

  const blurHandlerArray = async (name: keyof InitialValuesType, formProps: FormikProps<InitialValuesType>, event: any) => {
    const isProductArray = productArrayValues.includes(name)

    const enumArray = Object.values(isProductArray ? CustomerProduct : CustomerFeature)
    const mapping = isProductArray ? productsMapping : featuresMapping

    const productsArray = enumArray.filter((value) => {
      const key = mapping[value]

      // If the enum value is the currently active array field (the switch)
      // return the checked status as it has not been updated in formik by now
      if (key === name) {
        return event.target.checked
      }

      return formProps.values[key]
    })

    // Handle any formatting edgecases, default to the string value
    const formattedValue = formatValue(name, productsArray)

    const key = fieldToAPIMapping[name]

    if (!key) throw new Error(`'${key}' does not exist in BackOfficeCustomerDtoV2 or '${name}' does not exist in the mapping`)

    // Handle edgecase in spec where if we are enabling card product, we also enabled order card and card credit
    if (name === 'cardProduct' && !customerHasProduct(customer)) {
      const featuresKey = fieldToAPIMapping['orderCard']

      if (!featuresKey)
        throw new Error(`'${featuresKey}' does not exist in BackOfficeCustomerDtoV2 or 'cardCredit' does not exist in the mapping`)

      const features = Array.from(new Set([...Array.from(customer.features)]))

      // Empty strings should be null
      const promise = customerMutation({
        [key]: formattedValue,
        [featuresKey]: features,
      })

      setLoader(name, promise)

      return promise.catch(() => formProps.resetForm())
    }

    // Empty strings should be null
    const promise = customerMutation({
      [key]: formattedValue,
    })

    setLoader(name, promise)

    promise.catch(() => formProps.resetForm())
  }

  const refreshClick = async () => addressMutation.mutateAsync({ customerId: customer.id })

  const onHover = () => {
    setHover(true)
  }

  const onLeave = () => {
    setHover(false)
  }

  return (
    <FigmaBox spacing={Spacings.max} fullWidth>
      <Formik initialValues={initialValues} onSubmit={(...args) => console.log('submit', ...args)} enableReinitialize>
        {(formProps) => (
          <FigmaBox fullWidth direction="row">
            <FigmaBox style={{ width: '100%' }}>
              <BorderedContainer fullWidth>
                <FigmaBox fullWidth direction="row" align="center" justify="space-between" spacing={Spacings.medium} bottom>
                  <TextContainer textKey={TextKeys.GeneralInfoHeading} />
                  <Access permissions="customers.refresh-address">
                    <TooltipRefreshAddress
                      content={
                        <UnstyledButton onClick={refreshClick} onMouseEnter={onHover} onMouseLeave={onLeave}>
                          {!hover && <SVGContainer SVG={iconButtonrefreshAddress} />}
                          {hover && <SVGContainer SVG={iconButtonrefreshAddressHover} />}
                        </UnstyledButton>
                      }
                    />
                  </Access>
                </FigmaBox>
                <FigmaBox direction="row" fullWidth>
                  <FigmaBox spacing={Spacings.medium} right fullWidth>
                    <StyledMaterialFieldFormik
                      formProps={formProps}
                      fullWidth
                      name="companyName"
                      labelTextKey={TextKeys.companyNameLabel}
                      onBlur={createBlurHandler('companyName', formProps)}
                      loading={loaders['companyName']}
                      disabled={loaders['companyName']}
                    />
                  </FigmaBox>
                  <StyledMaterialFieldFormik
                    formProps={formProps}
                    fullWidth
                    name="orgNo"
                    labelTextKey={TextKeys.orgNumberLabel}
                    onBlur={createBlurHandler('orgNo', formProps)}
                    loading={loaders['orgNo']}
                    disabled={loaders['orgNo']}
                  />
                </FigmaBox>
                <FigmaBox direction="row" fullWidth>
                  <FigmaBox fullWidth>
                    <StyledMaterialFieldFormik
                      formProps={formProps}
                      fullWidth
                      name="addressField1"
                      labelTextKey={TextKeys.addressLabel}
                      onBlur={createBlurHandler('addressField1', formProps)}
                      loading={loaders['addressField1']}
                      disabled={loaders['addressField1']}
                    />
                  </FigmaBox>
                </FigmaBox>
                <FigmaBox direction="row" fullWidth>
                  <FigmaBox spacing={Spacings.medium} right fullWidth>
                    <StyledMaterialFieldFormik
                      formProps={formProps}
                      fullWidth
                      name="addressField2"
                      labelTextKey={TextKeys.addressCoLabel}
                      onBlur={createBlurHandler('addressField2', formProps)}
                      loading={loaders['addressField2']}
                      disabled={loaders['addressField2']}
                    />
                  </FigmaBox>
                  <StyledMaterialFieldFormik
                    noSpacing
                    formProps={formProps}
                    fullWidth
                    name="postTown"
                    labelTextKey={TextKeys.cityLabel}
                    onBlur={createBlurHandler('postTown', formProps)}
                    loading={loaders['postTown']}
                    disabled={loaders['postTown']}
                    error={formProps.errors.postTown}
                  />
                </FigmaBox>
                <FigmaBox direction="row" fullWidth>
                  <FigmaBox spacing={Spacings.medium} right fullWidth>
                    <StyledMaterialFieldFormik
                      noSpacing
                      formProps={formProps}
                      fullWidth
                      name="postCode"
                      labelTextKey={TextKeys.postalCodeLabel}
                      onBlur={createBlurHandler('postCode', formProps)}
                      loading={loaders['postCode']}
                      disabled={loaders['postCode']}
                      error={formProps.errors.postCode}
                    />
                  </FigmaBox>
                  <StyledMaterialFieldFormik
                    formProps={formProps}
                    fullWidth
                    name="postCountry"
                    labelText="Country"
                    onBlur={createBlurHandler('postCountry', formProps)}
                    loading={loaders['postCountry']}
                    disabled={loaders['postCountry']}
                  />
                </FigmaBox>
                <Divider onlyBottom />
                <Box sx={{ display: 'flex', gap: Spacings.medium, flexDirection: 'column' }}>
                  <FigmaBox direction="row" fullWidth>
                    <FigmaBox spacing={Spacings.tiny} right fullWidth>
                      <StyledMaterialFieldFormik
                        noSpacing
                        formProps={formProps}
                        fullWidth
                        name="invoiceEmail"
                        labelTextKey={TextKeys.invoiceEmailLabel}
                        onBlur={createBlurHandler('invoiceEmail', formProps)}
                        loading={loaders['invoiceEmail']}
                        disabled={loaders['invoiceEmail']}
                      />
                    </FigmaBox>
                    <FigmaBox spacing={Spacings.tiny} left fullWidth>
                      <StyledMaterialFieldFormik
                        noSpacing
                        formProps={formProps}
                        fullWidth
                        name="phoneNumber"
                        labelTextKey={TextKeys.phoneNumberLabel}
                        onBlur={createBlurHandler('phoneNumber', formProps)}
                        loading={loaders['phoneNumber']}
                        disabled={loaders['phoneNumber']}
                        type="tel"
                      />
                    </FigmaBox>
                  </FigmaBox>
                  <FigmaBox direction="row" fullWidth>
                    <FigmaBox spacing={Spacings.tiny} right fullWidth>
                      <TextFieldController
                        control={invoiceConfigForm.control}
                        name="eInvoiceAddress"
                        InputProps={{
                          defaultValue: invoiceConfig?.eInvoiceAddress,
                          onBlur: invoiceConfigForm.handleSubmit(handleInvoicePatchInvoiceConfig),
                          endAdornment: invoiceConfigMutation.isLoading ? (
                            <FigmaBox spacing={Spacings.small} right>
                              <CircularProgress size="20px" />
                            </FigmaBox>
                          ) : null,
                        }}
                        labelText="E-invoice address"
                      />
                    </FigmaBox>
                    <FigmaBox spacing={Spacings.tiny} left fullWidth>
                      <a
                        style={{ width: '100%', height: '100%' }}
                        href={`https://verkkolaskuosoite.fi/client/index.html#/?searchText=${customer.companyId?.regNo}&lang=en`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button fullWidth sx={{ height: '100%' }}>
                          Lookup E-invoice address (Finland)
                        </Button>
                      </a>
                    </FigmaBox>
                  </FigmaBox>
                  <FigmaBox direction="row" fullWidth>
                    <FigmaBox spacing={Spacings.tiny} right fullWidth>
                      <SelectController
                        disabled={invoiceConfigMutation.isLoading}
                        loading={invoiceConfigMutation.isLoading}
                        name="deliveryMethod"
                        control={invoiceConfigForm.control}
                        labelText="Invoice delivery method"
                        options={DeliveryMethodOptions}
                        onChange={onDeliveryMethodUpdate}
                      />
                    </FigmaBox>
                    <FigmaBox spacing={Spacings.tiny} left fullWidth />
                  </FigmaBox>
                  {cardCredit && (
                    <>
                      {cardCredit.isInvoicingActive == null && (
                        <Alert severity="warning">
                          This customer has not been handled yet: &quot;Invoicing active&quot; is not set. Select On or Off to decide
                          whether Mynt should invoice this customer for their debt.
                        </Alert>
                      )}
                      <FigmaBox direction="row" fullWidth>
                        <FigmaBox spacing={Spacings.tiny} right fullWidth>
                          <Tooltip title={invoicingActiveDisabledByExternal ? 'External credit types cannot be changed from off' : ''}>
                            <span style={{ width: '100%' }}>
                              <SelectController
                                disabled={cardCreditMutation.isLoading || invoicingActiveDisabledByExternal}
                                name="isInvoicingActive"
                                control={invoicingActiveForm.control}
                                labelText="Invoicing active"
                                options={InvoicingActiveOptions}
                                onBlur={onInvoicingActiveUpdate}
                              />
                            </span>
                          </Tooltip>
                        </FigmaBox>
                        <FigmaBox spacing={Spacings.tiny} left fullWidth />
                      </FigmaBox>
                    </>
                  )}
                </Box>
              </BorderedContainer>
              <BorderedContainer fullWidth>
                <FigmaBox spacing={Spacings.medium} bottom>
                  <TextContainer textKey={TextKeys.bankInfoHeading} />
                </FigmaBox>
                <FigmaBox direction="row" fullWidth>
                  <FigmaBox spacing={Spacings.tiny} right fullWidth>
                    <StyledMaterialFieldFormik
                      noSpacing
                      formProps={formProps}
                      fullWidth
                      name="bankAccount"
                      labelTextKey={TextKeys.bankAccountLabel}
                      onBlur={createBlurHandler('bankAccount', formProps)}
                      loading={loaders['bankAccount']}
                      disabled={loaders['bankAccount']}
                    />
                  </FigmaBox>
                  <FigmaBox spacing={Spacings.tiny} left fullWidth>
                    <StyledMaterialFieldFormik
                      noSpacing
                      formProps={formProps}
                      fullWidth
                      name="bankgiro"
                      labelTextKey={TextKeys.bankgiroLabel}
                      onBlur={createBlurHandler('bankgiro', formProps)}
                      loading={loaders['bankgiro']}
                      disabled={loaders['bankgiro']}
                    />
                  </FigmaBox>
                </FigmaBox>
              </BorderedContainer>
              <FigmaBox direction="row" fullWidth>
                <FigmaBox fullWidth spacing={Spacings.tiny} right>
                  <BorderedContainer fullWidth style={{ flex: 1 }}>
                    <FigmaBox spacing={Spacings.medium} bottom fullWidth>
                      <TextContainer textKey={TextKeys.AccountingInfoHeading} />
                    </FigmaBox>
                    <LedgerConnectionFactory customerId={customer.id} />
                  </BorderedContainer>
                </FigmaBox>
                <FigmaBox fullWidth spacing={Spacings.tiny} left>
                  <BorderedContainer fullWidth style={{ marginRight: 0, flex: 1 }}>
                    <FigmaBox spacing={Spacings.medium} bottom fullWidth>
                      <TextContainer textKey={TextKeys.acquisitionChannelHeading} />
                    </FigmaBox>
                    <StyledMaterialFieldFormik
                      noSpacing
                      formProps={formProps}
                      fullWidth
                      name="channel"
                      labelTextKey={TextKeys.channelLabel}
                      onBlur={createBlurHandler('channel', formProps)}
                      loading={loaders['channel']}
                      disabled={loaders['channel']}
                    />
                  </BorderedContainer>
                </FigmaBox>
              </FigmaBox>
              <BorderedContainer fullWidth>
                <FigmaBox fullWidth spacing={Spacings.medium} bottom>
                  <TextContainer textKey={TextKeys.cardPartnerHeading} />
                </FigmaBox>
                <FigmaBox fullWidth align="center" direction="row">
                  <FigmaBox fullWidth>
                    <MaterialSelectFieldFormik
                      formProps={formProps}
                      fullWidth
                      name="cardPartner"
                      options={sortedCardPartnerValues.map((value) => ({
                        label: value,
                        value,
                      }))}
                      labelTextKey={TextKeys.cardPartnerLabel}
                      onChange={createSelectChangeHandler('cardPartner', formProps)}
                      loading={loaders['cardPartner']}
                      disabled={loaders['cardPartner'] || customer.partnerDisconnected || !permissions.cardPartner}
                    />
                  </FigmaBox>
                  <FigmaBox fullWidth spacing={Spacings.medium} left>
                    <StyledMaterialFieldFormik
                      noSpacing
                      formProps={formProps}
                      fullWidth
                      disabled={customer.cardPartner === CardPartner.NONE || loaders['cardPartnerId'] || !permissions.cardPartner}
                      name="cardPartnerId"
                      labelTextKey={TextKeys.companyIDLabel}
                      onBlur={createBlurHandler('cardPartnerId', formProps)}
                      loading={loaders['cardPartnerId']}
                    />
                  </FigmaBox>
                  <FigmaBox fullWidth spacing={Spacings.medium} left>
                    <MyntPopper
                      visible={showTokenPopper}
                      noPadding
                      marginBottom={Spacings.large}
                      position="top"
                      anchorRef={tokenElementRef.current}
                      content={
                        <ModalConfirmDialog
                          onCancel={() => handleEditTokenDiscard(formProps)}
                          onConfirm={() => handleEditTokenSave(formProps)}
                          description={TextKeys.toolTipAreYouSureTextCompanyToken}
                        />
                      }
                    />
                    <FigmaBox fullWidth ref={tokenElementRef}>
                      <StyledMaterialFieldFormik
                        inputProps={{ ref: tokenRef }}
                        noSpacing
                        formProps={formProps}
                        fullWidth
                        name="cardPartnerToken"
                        disabled={!canEditToken || customer.partnerDisconnected || !permissions.cardPartner}
                        onBlur={() => setShowTokenPopper(true)}
                        labelTextKey={
                          formProps.values.cardPartnerToken ? TextKeys.companyTokenLabel : TextKeys.cardProductToolTipActiveStatus
                        }
                      />
                    </FigmaBox>
                  </FigmaBox>
                  <LinkButton
                    disabled={customer.cardPartner === CardPartner.NONE || customer.partnerDisconnected || !permissions.cardPartner}
                    onClick={handleEditCompanyToken}
                    spacing={Spacings.min}
                    left
                    textKey={TextKeys.companyTokenEdit}
                  />
                </FigmaBox>
              </BorderedContainer>
            </FigmaBox>
            <FigmaBox spacing={Spacings.medium} left style={{ width: '100%', maxWidth: '40%' }}>
              <BorderedContainer green={formProps.values.cardProduct} fullWidth>
                <FigmaBox spacing={Spacings.small} bottom direction="row" align="center" justify="space-between" fullWidth>
                  <TextContainer textKey={TextKeys.cardProduct} />
                  <MaterialSwitchFormik
                    formProps={formProps}
                    name="cardProduct"
                    disabled={loaders['cardProduct']}
                    onChange={createBlurHandler('cardProduct', formProps)}
                  />
                </FigmaBox>
                <FigmaBox spacing={Spacings.min} bottom>
                  <TextContainer textKey={TextKeys.cardFeatuersLabel} />
                </FigmaBox>
                <FigmaBox direction="row" align="center" justify="space-between" fullWidth>
                  <TextContainer textKey={TextKeys.orderCard} />
                  <MaterialSwitchFormik
                    formProps={formProps}
                    name="orderCard"
                    disabled={loaders['orderCard']}
                    onChange={createBlurHandler('orderCard', formProps)}
                  />
                </FigmaBox>
                <Divider spacing={Spacings.min} />
                <FigmaBox direction="row" align="center" justify="space-between" fullWidth>
                  <TextContainer textKey={TextKeys.makeTopUps} />
                  <MaterialSwitchFormik
                    formProps={formProps}
                    name="cardTopup"
                    disabled={loaders['cardTopup']}
                    onChange={createBlurHandler('cardTopup', formProps)}
                  />
                </FigmaBox>
                <Divider spacing={Spacings.min} />
                <FigmaBox direction="row" align="center" justify="space-between" fullWidth>
                  <TextContainer textKey={TextKeys.cardCreditProduct} />
                  <MaterialSwitchFormik
                    formProps={formProps}
                    name="cardCredit"
                    disabled={loaders['cardCredit']}
                    onChange={createBlurHandler('cardCredit', formProps)}
                  />
                </FigmaBox>
                {config.enableSendWelcomeEmail && (
                  <>
                    <Divider spacing={Spacings.min} />
                    <FigmaBox fullWidth direction="row" align="center" justify="space-between" top spacing={Spacings.small}>
                      <TextContainer textKey={TextKeys.cardCreditProductSendWelcomeEmail} />
                      <MyntPopper
                        visible={showSendWelcomeEmailPopper}
                        noPadding
                        position="top"
                        marginBottom={Spacings.medium}
                        anchorRef={sendWelcomeEmailElementRef.current}
                        content={
                          <SendWelcomeEmailPopperContent
                            onConfirm={handleSendWelcomeEmail}
                            onCancel={() => setShowSendWelcomeEmailPopper(false)}
                          />
                        }
                      />
                      <FigmaBox
                        align="center"
                        style={{ cursor: 'pointer' }}
                        ref={sendWelcomeEmailElementRef}
                        onClick={() => setShowSendWelcomeEmailPopper((prev) => !prev)}
                      >
                        <SVGContainer SVG={iconButtonEmail} />
                      </FigmaBox>
                    </FigmaBox>
                  </>
                )}
              </BorderedContainer>
              <BorderedContainer align="center" justify="center" fullWidth direction="row">
                <FigmaBox fullWidth>
                  <TextContainer textKey={TextKeys.activationGuideLabel} />
                </FigmaBox>
                <MaterialSelectFieldFormik
                  fullWidth
                  name="visibility"
                  formProps={formProps}
                  labelTextKey={TextKeys.approvalStatusLabel}
                  labelText="Visibility"
                  options={sortedActivationGuideValue.map((value) => ({
                    label: value,
                    value,
                  }))}
                  onChange={handleActivationGuideStatus('visibility', formProps)}
                  loading={loaders['visibility']}
                  disabled={loaders['visibility'] || !permissions.activationGuide}
                />
              </BorderedContainer>
              <BorderedContainer align="center" justify="center" fullWidth direction="row">
                <FigmaBox fullWidth>
                  <TextContainer textKey={TextKeys.activationGuideLabel} text="KYC" />
                </FigmaBox>
                <MaterialSelectFieldFormik
                  fullWidth
                  name="kycAccessLevel"
                  formProps={formProps}
                  labelText="Status"
                  options={kycAccessLevelOptions.map((option) => ({
                    label: (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: accountStatusToColor[option.value],
                          }}
                        />
                        {option.label}
                      </Box>
                    ),
                    value: option.value,
                  }))}
                  onChange={createSelectChangeHandler('kycAccessLevel', formProps)}
                  loading={loaders['kycAccessLevel']}
                  disabled={loaders['kycAccessLevel']}
                />
              </BorderedContainer>
              <Access permissions="customers.delete">
                <BorderedContainer direction="row" align="center" justify="space-between" fullWidth>
                  <Typography variant="body2" fontWeight="bold">
                    Delete customer
                  </Typography>
                  <MyntPopper
                    visible={showDeleteCustomerPopper}
                    noPadding
                    marginBottom={Spacings.large}
                    position="top"
                    anchorRef={deleteCustomerElementRef.current}
                    content={
                      <DeleteCustomerPopper onConfirm={() => handleCustomerDelete()} onCancel={() => setShowDeleteCustomerPopper(false)} />
                    }
                  />
                  <FigmaBox
                    align="center"
                    style={{ cursor: 'pointer' }}
                    ref={deleteCustomerElementRef}
                    onClick={() => setShowDeleteCustomerPopper(!showDeleteCustomerPopper)}
                  >
                    <DeleteCustomerIcon />
                  </FigmaBox>
                </BorderedContainer>
              </Access>
              <Access permissions="customers.churn">
                <BorderedContainer direction="row" align="center" justify="space-between" fullWidth>
                  <Typography variant="body2" fontWeight="bold">
                    Churn customer
                  </Typography>
                  <ChurnCustomerModal
                    customerId={customer.id}
                    companyName={customer.companyName}
                    isOpen={isChurnModalOpen}
                    onSuccess={handleOnCustomerChurnSuccess}
                    onCancel={() => setIsChurnModalOpen(false)}
                  />
                  <FigmaBox
                    align="center"
                    style={{ cursor: 'pointer' }}
                    ref={churnCustomerElementRef}
                    onClick={() => setIsChurnModalOpen(true)}
                  >
                    <ChurnCustomerIcon />
                  </FigmaBox>
                </BorderedContainer>
              </Access>
            </FigmaBox>
          </FigmaBox>
        )}
      </Formik>
    </FigmaBox>
  )
}

export default CustomerGeneralForm
